import { InboxOutlined } from "@ant-design/icons";
import { Alert, Button, Popconfirm, Space, Switch, Table, Tag, Typography, Upload, message } from "antd";
import { type ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

import { type HelpMediaEntity } from "@fitness-app/data-models/entities/HelpArticle";

import { supabase } from "~/store/initializeStore";
import { helpMediaKind, HELP_MEDIA_EXTENSIONS } from "./helpMediaFile";
import {
  findUnusedHelpMedia,
  useHelpMediaThumbnails,
  useDeleteHelpMedia,
  useHelpArticles,
  useHelpMedia,
  useUploadHelpMedia,
  HELP_MEDIA_BUCKET,
} from "./useHelpCenter";

const formatBytes = (bytes: number) =>
  bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} kB`;

const KIND_LABELS: Record<string, string> = {
  image: "Obraz",
  video: "Film",
  captions: "Napisy",
  unknown: "Inny",
};

export const HelpMediaTab = () => {
  const mediaQuery = useHelpMedia();
  const articlesQuery = useHelpArticles();
  const uploadMedia = useUploadHelpMedia();
  const deleteMedia = useDeleteHelpMedia();
  const [onlyUnused, setOnlyUnused] = useState(false);
  const thumbnailPaths = useMemo(
    () =>
      (mediaQuery.data ?? []).flatMap((file) =>
        helpMediaKind(file.mimeType) === "image" ? [file.storagePath] : file.posterPath ? [file.posterPath] : [],
      ),
    [mediaQuery.data],
  );
  const thumbnails = useHelpMediaThumbnails(thumbnailPaths);

  const unused = useMemo(
    () => findUnusedHelpMedia(mediaQuery.data ?? [], articlesQuery.data ?? []),
    [articlesQuery.data, mediaQuery.data],
  );
  const unusedPaths = useMemo(() => new Set(unused.map((file) => file.storagePath)), [unused]);
  const rows = onlyUnused ? unused : (mediaQuery.data ?? []);

  const openPreview = async (media: HelpMediaEntity) => {
    const { data, error } = await supabase.storage.from(HELP_MEDIA_BUCKET).createSignedUrl(media.storagePath, 600);

    if (error) {
      void message.error("Nie udało się otworzyć podglądu.");
      return;
    }

    window.open(data.signedUrl, "_blank", "noopener");
  };

  const columns: ColumnsType<HelpMediaEntity> = [
    {
      title: "Plik",
      dataIndex: "storagePath",
      width: 320,
      // Nazwa powstaje z hasha zawartości, więc bez miniatury nie da się
      // rozpoznać, który to zrzut.
      render: (storagePath: string, media) => {
        const thumbnail = thumbnails.data?.[media.posterPath ?? storagePath];
        return (
          <Space size={10} align="start">
            {thumbnail ? (
              <img
                src={thumbnail}
                alt=""
                style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 4, display: "block" }}
              />
            ) : (
              <span
                style={{ width: 44, height: 44, borderRadius: 4, background: "rgba(0,0,0,0.06)", display: "block" }}
              />
            )}
            <Space direction="vertical" size={0}>
              <Typography.Text code copyable>
                {storagePath}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {KIND_LABELS[helpMediaKind(media.mimeType)]} · {formatBytes(media.bytes)}
              </Typography.Text>
              {media.captionsPath && (
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  napisy: {media.captionsPath}
                </Typography.Text>
              )}
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Wymiary",
      key: "size",
      width: 140,
      render: (_value, media) => (media.width && media.height ? `${media.width} × ${media.height}` : "—"),
    },
    {
      title: "Czas",
      dataIndex: "durationSeconds",
      width: 100,
      render: (duration: number | null) => (duration ? `${duration} s` : "—"),
    },
    {
      title: "Użycie",
      key: "usage",
      width: 140,
      render: (_value, media) =>
        unusedPaths.has(media.storagePath) ? (
          <Tag color="orange">Nieużywany</Tag>
        ) : (
          <Tag color="green">W materiale</Tag>
        ),
    },
    {
      title: "Wgrany",
      dataIndex: "createdAt",
      width: 140,
      render: (createdAt: string) => dayjs(createdAt).format("DD.MM.YYYY"),
    },
    {
      title: "Akcje",
      key: "actions",
      width: 200,
      render: (_value, media) => (
        <Space size={4}>
          <Button size="small" onClick={() => void openPreview(media)}>
            Podgląd
          </Button>
          <Popconfirm
            title="Usunąć plik?"
            description="Materiał, który go używa, zostanie z pustym miejscem."
            okText="Usuń"
            cancelText="Anuluj"
            onConfirm={() => deleteMedia.mutate(media)}
          >
            <Button size="small" danger disabled={!unusedPaths.has(media.storagePath)}>
              Usuń
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Upload.Dragger
        multiple
        accept={Object.keys(HELP_MEDIA_EXTENSIONS).join(",")}
        showUploadList={false}
        beforeUpload={(file) => {
          uploadMedia.mutate(file);
          // Wgrywamy sami, przez klienta Supabase — komponent antd ma tylko
          // podać plik.
          return Upload.LIST_IGNORE;
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Przeciągnij pliki albo kliknij, żeby wybrać</p>
        <p className="ant-upload-hint">PNG, JPG, WEBP, MP4 i napisy VTT. Nazwa powstaje z zawartości pliku.</p>
      </Upload.Dragger>

      <Alert
        type="info"
        showIcon
        message="Obrazy przechodzą przez przeglądarkę bez metadanych EXIF, a ich wymiary trafiają do wpisu."
        description="Bez wymiarów układ strony skacze przy dogrywaniu obrazka. Przy filmie zapisujemy też czas trwania — klientka widzi go przed odtworzeniem."
      />

      <Space>
        <Switch checked={onlyUnused} onChange={setOnlyUnused} />
        <Typography.Text>Pokaż tylko nieużywane ({unused.length})</Typography.Text>
      </Space>

      <Table<HelpMediaEntity>
        rowKey="id"
        loading={mediaQuery.isLoading || uploadMedia.isLoading}
        dataSource={rows}
        columns={columns}
        pagination={false}
        scroll={{ x: 940 }}
      />
    </Space>
  );
};
